import type { IHeaderParams } from 'ag-grid-community';
import type { BombiixRow } from '../types';

export type ServerSortOrder = 'Asc' | 'Desc' | '';

export interface ServerSortHeaderParams<TRow extends BombiixRow = BombiixRow> extends IHeaderParams<TRow> {
  /** Field name handed back to the caller, which reloads the page sorted by it. */
  sortField?: string;
  /** The field the list is currently sorted by, as the caller's own sort state reports it. */
  activeSortField?: string;
  /** Direction of that sort, in the caller's vocabulary. */
  activeSortOrder?: ServerSortOrder;
  onSort?: (_field: string) => void;
  /** Forwarded by BombiixAGGrid from the colDef; renders the red mandatory asterisk. */
  required?: boolean;
  /** Forwarded by BombiixAGGrid from the colDef; renders the green DPP-required asterisk. */
  dppRequired?: boolean;
}

/**
 * ServerSortHeader — a `SortableHeader` twin for grids whose sort is resolved by
 * the API, not by AG Grid.
 *
 * `SortableHeader` drives AG Grid's own column state, which reorders only the
 * rows already in the client. The Partner Overview lists page their rows in, so
 * sorting one page of them is not the same operation as sorting the list: the
 * caller owns the sort, refetches, and hands the order back. This header
 * therefore reports the caller's sort state rather than the column's, and its
 * columns are declared `sortable: false` so AG Grid never re-orders on top of
 * the answer the server gave.
 * @param {ServerSortHeaderParams} props - AG Grid header params plus the sort wiring.
 * @returns {React.ReactElement} The header cell.
 */
function ServerSortHeader<TRow extends BombiixRow = BombiixRow>(props: ServerSortHeaderParams<TRow>) {
  const { displayName, sortField, activeSortField, activeSortOrder, onSort, required, dppRequired } = props;
  const isActive = Boolean(sortField) && sortField === activeSortField;
  const ascending = isActive && activeSortOrder === 'Asc';
  const descending = isActive && activeSortOrder === 'Desc';
  const canSort = Boolean(sortField && onSort);

  return (
    <div className="cag-header">
      <button type="button" className="cag-header-label-btn" disabled={!canSort} onClick={() => sortField && onSort?.(sortField)}>
        <span className="cag-header-label">
          {displayName}
          {required || dppRequired ? <span className={`cag-header-required${dppRequired ? ' cag-header-required--green' : ''}`}>*</span> : null}
        </span>
        <span className="ag-sort-indicator-container">
          <span className={`ag-sort-indicator-icon ag-sort-ascending-icon${ascending ? '' : ' ag-hidden'}`}>
            <span className="ag-icon ag-icon-asc" role="presentation" />
          </span>
          <span className={`ag-sort-indicator-icon ag-sort-descending-icon${descending ? '' : ' ag-hidden'}`}>
            <span className="ag-icon ag-icon-desc" role="presentation" />
          </span>
        </span>
      </button>
    </div>
  );
}

export default ServerSortHeader;
